import {BehaviorSubject, EMPTY, firstValueFrom, Observable, switchMap} from 'rxjs';
import {map, shareReplay} from 'rxjs/operators';
import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {Router} from '@angular/router';
import {NotificationService} from './notification.service';
import {LocalStorageService} from './local-storage.service';
import {LoginSessionService} from './login-session.service';
import {isPlatformBrowser} from '@angular/common';
import {LoginResponseApiModel, LoginService} from "api-client";
import {ConstantsService} from "./constants.service";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

    user$ = new BehaviorSubject<string | null>(null);
    private rightsDefaults = {
        changePlan: true,
        changePodioAccount: true,
        changePassword: true,
        changeEmailNotification: true,
        viewAdminPage: false
    };
    private rights = {...this.rightsDefaults};
    private loading$ = new BehaviorSubject(false);

    private storageName = 'podio_backup_login_session';

    loggedIn$ = this.user$.pipe(map(user => user != null), shareReplay(1));
    /** emits true/false if user is logged in. No event is emitted while loading is in progress */
    loggedInOrLoading$: Observable<boolean> = this.loading$.pipe(
      switchMap(loading => loading ? EMPTY : this.loggedIn$)
    );

    redirectUrl$ = new BehaviorSubject<string>('/view/account');

    constructor(private loginService: LoginService,
                private constants: ConstantsService,
                private notify: NotificationService,
                private router: Router,
                private localStorage: LocalStorageService,
                private loginSessionService: LoginSessionService,
                @Inject(PLATFORM_ID) private platformId: Object) {

        if (isPlatformBrowser(this.platformId)) {
          // router.url is not correctly initialized yet..
          const queryParamMap = router.parseUrl(window.location.pathname + window.location.search).queryParamMap;
          // TODO clear search/query without annoying angular.. (Prio B, as automatically cleared on first link click..)
          // window.location.search = '';
          const oauthCode = queryParamMap.get('code');
          if (oauthCode) {
            this.processOauthLogin(oauthCode, queryParamMap.get('state'));
          } else {
            console.log('loginsession from localStorage: ', this.localStorage.get(this.storageName));
            if (this.localStorage.get(this.storageName) !== null) {
              this.loginSessionService.loginSession = this.localStorage.get(this.storageName);
              this.loading$.next(true);
              this.loginService.login().pipe(
                map(response => this.mapLoginResponse(response, true)))
                .subscribe(() => {
                  console.log('logged in via login session.');
                }, error => {
                  this.notify.notify(error.message, 'Could not log in!', 'warning');
                  this.clear();
                });
            }
          }
        }
    }

  private async processOauthLogin(oauthCode: string, state: string | null) {
    this.loading$.next(true);
    try {
      // path is e.g.: /oauth/register/cbfp-test?code=8131774cf535e0612d60dbb59c064bbf&state=%7B%22promo%22:%22%22,%22redirect%22:%22%2Fview%2Faccount%22%7D
      const appId = window.location.pathname.split('/')[3];
      const login = window.location.pathname.split('/')[4];
      const decodedLogin = login ? decodeURIComponent(login) : null;
      const promo = state ? JSON.parse(state)['promo'] : undefined;
      const response = await firstValueFrom(this.loginService.registerOAuthCode(appId, oauthCode, decodedLogin, promo));
      const users = response.matchedUsers ?? [];
      if (users.length > 1) {
        const result = await this.notify.choose('Multiple users found!', 'Please select login:', users)
        await this.loginOauth(oauthCode, result);
      } else {
        await this.loginOauth(oauthCode);
      }
      const redirect = state ? JSON.parse(state)['redirect'] : null;
      if (redirect) {
        await this.router.navigateByUrl(redirect);
      }
    } catch (error) {
      this.notify.notify(error.message, 'Could not log in!', 'warning');
      this.clear();
    }
  }

  /**
     * After this url is called, after any login, user will be redirected to given url.
     * (Except fixed redirects, e.g. register..)
     * @param url
     */
    fixedLoginRedirect(url: string) {
        this.redirectUrl$.next(url);
    }

  private async loginOauth(oauthCode: string, login: string = null) {
      const r = await firstValueFrom(this.loginService.login('Bearer ' + oauthCode, login ? btoa(login) : null));
      this.mapLoginResponse(r, true)
      console.log('logged in via oauth.');
  }

    login(username: string, password: string) {
        this.loading$.next(true);
        return this.loginService.login('Basic ' + btoa(username + ':' + password)).pipe(
            map(response => this.mapLoginResponse(response, false))).toPromise();
    }

    /**
     *
     * @param loginResponse
     * @param autoLogin skips redirect and stays on current page!
     */
    public mapLoginResponse(loginResponse: LoginResponseApiModel | undefined, autoLogin: boolean) {
        // login successful if there's a jwt token in the response
        if (loginResponse && loginResponse.loginsession) {
            // store user details and jwt token in local storage to keep user logged in between page refreshes
            this.localStorage.set(this.storageName, loginResponse.loginsession);
            this.user$.next(loginResponse.user);
            this.rights = {...this.rights};
            // only update rights that are defined and non-null in the responseL
            Object.keys(loginResponse.rights ?? {}).forEach(key => {
              if (loginResponse.rights[key] !== null) {
                this.rights[key] = loginResponse.rights[key];
              }
            });
            this.loginSessionService.loginSession = loginResponse.loginsession;
            this.constants.setDownloadToken(loginResponse.downloadToken ?? null);
            this.loading$.next(false);
            console.log('login successful: ' + this.localStorage.get(this.storageName));

            if (!autoLogin && this.redirectUrl$.value) {
                //noinspection JSIgnoredPromiseFromCall
                this.router.navigateByUrl(this.redirectUrl$.value);
            }
        } else {
            this.clear();
        }
    }

    private clear() {
        this.localStorage.remove(this.storageName);
        this.user$.next(null);
        this.rights = {...this.rightsDefaults};
        this.loginSessionService.loginSession = null;
        this.constants.setDownloadToken(null);
        this.loading$.next(false);
    }

    // TODO reactive?
    hasRight(right: string) {
        return !!this.rights[right];
    }

    logout() {
        this.loading$.next(true);
        this.loginService.logout().toPromise()
            .then(result => console.log('Logged out: ', result))
            .catch(error => console.log('Logout failed: ', error));
        this.clear();
        this.router.navigateByUrl('/')
    }
}
